import { useState, useRef, useEffect, forwardRef } from 'react'
import { Input } from '../ui/input'
import { useComposerAutocompleteWithFallback } from '../hooks/useComposerAutocompleteWithFallback'
import type { Composer } from '../hooks/useComposerAutocomplete'
import type { SearchComposersResponse } from '@brent/shared/types'

interface ComposerAutocompleteInputProps extends Omit<
  React.ComponentProps<typeof Input>,
  'value' | 'onChange'
> {
  value: string
  onChange: (value: string) => void
  onComposerSelect?: (composer: Composer | null) => void
  searchFn?: (term: string, limit: number) => Promise<SearchComposersResponse>
}

/**
 * Autocomplete input for composer names with client-side fuzzy search
 */
export const ComposerAutocompleteInput = forwardRef<
  HTMLInputElement,
  ComposerAutocompleteInputProps
>(
  (
    {
      value,
      onChange,
      onComposerSelect,
      searchFn,
      onBlur,
      disabled = false,
      placeholder = 'e.g., Ludwig van Beethoven',
      ...props
    },
    ref,
  ) => {
    const [isFocused, setIsFocused] = useState(false)
    const [selectedIndex, setSelectedIndex] = useState(-1)
    const internalRef = useRef<HTMLInputElement>(null)
    const dropdownRef = useRef<HTMLDivElement>(null)

    // Use either the forwarded ref or our internal ref
    const inputRef = (ref as React.RefObject<HTMLInputElement>) || internalRef

    // Get autocomplete results with Lambda fallback
    const {
      composers: suggestions,
      isLoading,
      error,
      source,
    } = useComposerAutocompleteWithFallback(value, searchFn)

    // Show dropdown when focused and (has suggestions OR loading OR can add freetext)
    const showDropdown = isFocused && value.length >= 2 && (suggestions.length > 0 || isLoading)

    // Show "Add anyway" option whenever user has typed enough (even while loading)
    const showFreetextOption = isFocused && value.length >= 2

    // Handle selecting a composer from the dropdown
    const selectComposer = (composer: Composer) => {
      onChange(composer.name)
      onComposerSelect?.(composer)
      setIsFocused(false)
      setSelectedIndex(-1)
      inputRef.current?.blur()
    }

    // Handle adding as freetext entry
    const addAsFreetext = () => {
      const freetextComposer: Composer = {
        id: 'FREETEXT', // Special marker for freetext entries (no real Wikidata ID)
        name: value,
        searchTerms: [],
        popularity: 0,
        source: 'freetext',
      }
      onComposerSelect?.(freetextComposer)
      setIsFocused(false)
      inputRef.current?.blur()
    }

    // Track when the input value changes to detect clearing
    const handleChange = (newValue: string) => {
      onChange(newValue)
      // If value is cleared, notify parent
      if (newValue === '' && onComposerSelect) {
        onComposerSelect(null)
      }
    }

    // Handle keyboard navigation
    const handleKeyDown = (e: React.KeyboardEvent) => {
      if (!showDropdown) return

      switch (e.key) {
        case 'ArrowDown':
          e.preventDefault()
          setSelectedIndex((prev) => (prev < suggestions.length - 1 ? prev + 1 : prev))
          break
        case 'ArrowUp':
          e.preventDefault()
          setSelectedIndex((prev) => (prev > 0 ? prev - 1 : -1))
          break
        case 'Enter':
          e.preventDefault()
          if (selectedIndex >= 0 && suggestions[selectedIndex]) {
            selectComposer(suggestions[selectedIndex])
          }
          break
        case 'Escape':
          e.preventDefault()
          setIsFocused(false)
          setSelectedIndex(-1)
          break
      }
    }

    // Scroll selected item into view
    useEffect(() => {
      if (selectedIndex >= 0 && dropdownRef.current) {
        const selectedItem = dropdownRef.current.children[selectedIndex] as HTMLElement
        selectedItem?.scrollIntoView({ block: 'nearest', behavior: 'smooth' })
      }
    }, [selectedIndex])

    // Reset selected index when suggestions change
    useEffect(() => {
      setSelectedIndex(-1)
    }, [suggestions])

    return (
      <div className="relative">
        <Input
          {...props}
          ref={ref || internalRef}
          value={value}
          onChange={(e) => handleChange(e.target.value)}
          onFocus={() => setIsFocused(true)}
          onBlur={(e) => {
            // Delay blur to allow click events on dropdown items
            setTimeout(() => {
              setIsFocused(false)
              setSelectedIndex(-1)
              onBlur?.(e)
            }, 200)
            // Call the blur handler from the ref if it exists
            if (ref && typeof ref !== 'function' && ref.current) {
              ref.current.blur()
            }
          }}
          onKeyDown={handleKeyDown}
          disabled={disabled}
          placeholder={placeholder}
          inputMode="search"
          autoComplete="off"
          aria-autocomplete="list"
          aria-expanded={showDropdown}
          aria-controls={showDropdown ? 'composer-suggestions' : undefined}
        />

        {(showDropdown || showFreetextOption) && (
          <div
            id="composer-suggestions"
            ref={dropdownRef}
            className="absolute z-50 mt-1 w-full max-h-60 overflow-y-auto rounded-md border bg-popover text-popover-foreground shadow-md"
            role="listbox"
          >
            {isLoading && (
              <div className="px-3 py-2 text-sm text-muted-foreground">Searching Wikidata...</div>
            )}

            {error && !isLoading && (
              <div className="px-3 py-2 text-sm text-destructive">
                Failed to search: {error.message}
              </div>
            )}

            {!isLoading &&
              suggestions.map((composer, index) => (
                <div
                  key={composer.id}
                  role="option"
                  aria-selected={selectedIndex === index}
                  className={`
                cursor-pointer px-3 py-2 text-sm
                ${selectedIndex === index ? 'bg-accent text-accent-foreground' : 'hover:bg-accent/50'}
              `}
                  onClick={() => selectComposer(composer)}
                  onMouseEnter={() => setSelectedIndex(index)}
                >
                  <div className="flex items-center justify-between">
                    <div className="font-medium">{composer.name}</div>
                    {composer.source === 'freetext' && (
                      <span className="ml-2 inline-flex items-center rounded-full bg-yellow-100 px-2 py-0.5 text-xs font-medium text-yellow-800">
                        Custom Entry
                      </span>
                    )}
                  </div>
                  {(composer.birth || composer.death) && (
                    <div className="text-xs text-muted-foreground">
                      {composer.birth && composer.death
                        ? `${composer.birth}–${composer.death}`
                        : composer.birth
                          ? `b. ${composer.birth}`
                          : `d. ${composer.death}`}
                    </div>
                  )}
                  {source === 'lambda' && (
                    <div className="text-xs text-muted-foreground italic">from Wikidata</div>
                  )}
                </div>
              ))}

            {showFreetextOption && (
              <div
                role="option"
                className="cursor-pointer border-t px-3 py-2 text-sm hover:bg-accent/50"
                onClick={addAsFreetext}
              >
                <div className="font-medium text-muted-foreground">
                  Can't find it? Add "{value}" anyway
                </div>
                <div className="text-xs text-muted-foreground">
                  This will be saved as a custom entry
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    )
  },
)

ComposerAutocompleteInput.displayName = 'ComposerAutocompleteInput'
